import { UsersIcon } from 'lucide-react';
import { useCallback, type FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import { useManagerViewEnabled, useViewMode, type ViewMode } from '../hooks/use-view-mode.ts';
import { Menu, MenuDrillRow, MenuOptionRow } from './menu-primitives.tsx';

/** The view the user is currently in, for the collapsed "View as" row. */
const useCurrentViewName = (): string => {
    const { t } = useLocale('common');
    const { viewMode } = useViewMode();
    return viewMode === 'MANAGER' ? t('view_switch__manager') : t('view_switch__sales_rep');
};

export type ViewAsRowProps = {
    onOpen: () => void;
};

/**
 * "View as … Manager" — the collapsed row on the settings level. Renders
 * nothing unless the manager experience is active (manager role), so it is
 * safe to mount unconditionally. Reps never see a view they cannot switch to.
 */
export const ViewAsRow: FC<ViewAsRowProps> = ({ onOpen }) => {
    const { t } = useLocale('common');
    const managerViewEnabled = useManagerViewEnabled();
    const currentViewName = useCurrentViewName();

    if (!managerViewEnabled) {
        return null;
    }

    return (
        <MenuDrillRow
            icon={UsersIcon}
            label={t('view_switch__view_as_label')}
            value={currentViewName}
            onOpen={onOpen}
        />
    );
};

export type ViewAsRowsProps = {
    /** Called after picking the view already in use — pops back a level. */
    onPicked?: () => void;
    /** Called after an actual switch, so the containing surface can dismiss. */
    onSwitched?: () => void;
};

/** The two views as options, check-marked on the current one. */
export const ViewAsRows: FC<ViewAsRowsProps> = ({ onPicked, onSwitched }) => {
    const { t } = useLocale('common');
    const navigate = useNavigate();
    const { viewMode, setViewMode } = useViewMode();

    const pick = useCallback(
        (next: ViewMode) => {
            if (next === viewMode) {
                onPicked?.();
                return;
            }
            setViewMode(next);
            void navigate('/', { replace: true });
            onSwitched?.();
        },
        [viewMode, setViewMode, navigate, onPicked, onSwitched],
    );

    return (
        <Menu className="p-0">
            <MenuOptionRow
                label={t('view_switch__manager')}
                selected={viewMode === 'MANAGER'}
                onSelect={() => {
                    pick('MANAGER');
                }}
            />
            <MenuOptionRow
                label={t('view_switch__sales_rep')}
                selected={viewMode === 'REP'}
                onSelect={() => {
                    pick('REP');
                }}
            />
        </Menu>
    );
};
